import { Localized } from "@fluent/react"
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import AddIcon from "@mui/icons-material/Add"
import ImportantDevicesIcon from "@mui/icons-material/ImportantDevices"
import { Box, Button, Paper, Stack, Typography } from "@mui/material"
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { unwrapResult } from "@reduxjs/toolkit"
import { LoadingContainer } from "../../app/LoadingContainer"
import { useAppDispatch } from "../../app/hooks"
import { CreateStructureDialog } from "./dialogs/CreateStructureDialog"
import { loadCompanyStructuresDefinitions, StructureDefinitionDto } from "./structureSlice"
import { ErrorContainer } from "../../app/ErrorContainer"

export const CompanyStructureDefinitions = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [definitions, setDefinitions] = useState<StructureDefinitionDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [configurationCode, setConfigurationCode] = useState<string | undefined>()

    useEffect(() => {
        setIsLoading(true)
        setIsError(false)
        setError(null)
        dispatch(loadCompanyStructuresDefinitions({ onlyActive: true }))
            .then(unwrapResult)
            .then(setDefinitions)
            .catch(error => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false))
    }, [dispatch])

    const organizationalStructure = useMemo(
        () => definitions.find(definition => definition.type === "ORGANIZATIONAL_STRUCTURE"),
        [definitions]
    )

    const deviceStructure = useMemo(
        () => definitions.find(definition => definition.type === "CUSTOM_DEVICE_STRUCTURE"),
        [definitions]
    )

    const customStructures = useMemo(
        () => definitions.filter(definition => definition.type !== "ORGANIZATIONAL_STRUCTURE" && definition.type !== "CUSTOM_DEVICE_STRUCTURE"),
        [definitions]
    )

    const closeFormDialog = useCallback(() => {
        setCreateDialogOpen(false)
        setConfigurationCode(undefined)
    }, [])

    const onSuccessDialog = useCallback((definition: StructureDefinitionDto) => {
        setDefinitions(prevDefinitions => [...prevDefinitions, definition]);
        closeFormDialog();
    }, [closeFormDialog])

    if (isLoading) {
        return <LoadingContainer />
    }

    if (isError) {
        return <ErrorContainer error={error} />
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                height: "calc(100vh - 112px)",
                minHeight: 0,
                overflow: "hidden",
            }}
        >
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <Localized id="structures-overview-title">Struktury</Localized>
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.25 }}>
                    <Localized id="structures-overview-subtitle">
                        Zarządzaj strukturą organizacyjną i dodatkowymi strukturami firmy.
                    </Localized>
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.5,
                }}
            >
                <OverviewCard
                    icon={<AccountTreeOutlinedIcon />}
                    title={<Localized id="structures-overview-organization">Organizacja</Localized>}
                    description={
                        <Localized id="structures-overview-organization-description">
                            Działy i jednostki organizacyjne firmy.
                        </Localized>
                    }
                    status={
                        <Localized id={organizationalStructure
                            ? "structures-overview-configured"
                            : "structures-overview-not-configured"}
                        >
                            {organizationalStructure ? "Skonfigurowano" : "Nie skonfigurowano"}
                        </Localized>
                    }
                    actionLabel={
                        <Localized id={organizationalStructure
                            ? "structures-overview-open"
                            : "structures-overview-configure"}
                        >
                            {organizationalStructure ? "Otwórz strukturę" : "Skonfiguruj"}
                        </Localized>
                    }
                    onAction={() => {
                        if (organizationalStructure) {
                            navigate("/company-structures/organization")
                        } else {
                            setConfigurationCode("ORGANIZATIONAL_STRUCTURE")
                            setCreateDialogOpen(true)
                        }
                    }}
                    isConfigured={!!organizationalStructure}
                />

                <OverviewCard
                    icon={<ImportantDevicesIcon />}
                    title={<Localized id="structures-overview-custom-device-structure">Własna struktura urządzeń</Localized>}
                    description={
                        <Localized id="structures-overview-custom-device-structure-description">
                            Własna struktura do porządkowania urządzeń.
                        </Localized>
                    }
                    status={
                        <Localized id={deviceStructure
                            ? "structures-overview-configured"
                            : "structures-overview-not-configured"}
                        >
                            {deviceStructure ? "Skonfigurowano" : "Nie skonfigurowano"}
                        </Localized>
                    }
                    actionLabel={
                        <Localized id={deviceStructure
                            ? "structures-overview-open"
                            : "structures-overview-configure"}
                        >
                            {deviceStructure ? "Otwórz strukturę" : "Skonfiguruj"}
                        </Localized>
                    }
                    onAction={() => {
                        if (deviceStructure) {
                            navigate(`/company-structures/edit/${deviceStructure.id}`)
                        } else {
                            setConfigurationCode("CUSTOM_DEVICE_STRUCTURE")
                            setCreateDialogOpen(true)
                        }
                    }}
                    isConfigured={!!deviceStructure}
                />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ mb: 1 }}
                >
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            <Localized id="structures-overview-custom">Struktury własne</Localized>
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.25 }}>
                            <Localized id="structures-overview-custom-description">
                                Dodatkowe drzewa zdefiniowane przez Twoją firmę.
                            </Localized>
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setConfigurationCode(undefined)
                            setCreateDialogOpen(true)
                        }}
                        sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                    >
                        <Localized id="structures-overview-add">Dodaj własną strukturę</Localized>
                    </Button>
                </Stack>

                <Paper
                    variant="outlined"
                    sx={{
                        height: "calc(100vh - 460px)", 
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto"
                    }}
                >
                    <Box sx={{ flex: 1, overflowX: "hidden" }}>
                        {customStructures.length > 0 ? (
                            customStructures.sort((a, b) => a.name.localeCompare(b.name)).map((structure) => (
                                <Box
                                    key={structure.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1.5,
                                        p: { xs: 1.5, sm: 2 },
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 600 }}>{structure.name}</Typography>
                                        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>
                                            {structure.description || structure.code}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate(`/company-structures/edit/${structure.id}`)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        <Localized id="structures-overview-open">Otwórz strukturę</Localized>
                                    </Button>
                                </Box>
                            ))
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                    minHeight: 160,
                                    px: 2,
                                    py: 2,
                                    textAlign: "center",
                                }}
                            >
                                <AccountTreeOutlinedIcon
                                    sx={{
                                        fontSize: 48,
                                        color: "action.disabled",
                                        mb: 1.5,
                                    }}
                                />
                                <Typography color="text.secondary">
                                    <Localized id="structures-overview-empty">
                                        Nie masz jeszcze własnych struktur. Dodaj pierwszą, aby rozpocząć.
                                    </Localized>
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            <CreateStructureDialog
                open={createDialogOpen}
                defaultCode={configurationCode}
                onClose={() => closeFormDialog()}
                onSaved={onSuccessDialog}
            />
        </Box>
    )
}

interface OverviewCardProps {
    icon: ReactNode
    title: ReactNode
    description: ReactNode
    status: ReactNode
    actionLabel: ReactNode
    onAction: () => void
    isConfigured: boolean
}

const OverviewCard = ({
    icon,
    title,
    description,
    status,
    actionLabel,
    onAction,
    isConfigured,
}: OverviewCardProps) => (
    <Paper
        variant="outlined"
        sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minHeight: 190,
        }}
    >
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" spacing={1}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    color: "secondary.main",
                    bgcolor: "action.hover",
                }}
            >
                {icon}
            </Box>
            <Typography variant="body2" color="text.secondary">
                {status}
            </Typography>
        </Stack>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1.5 }}>
            {title}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, flexGrow: 1 }}>
            {description}
        </Typography>
        <Button
            variant={isConfigured ? "contained" : "outlined"}
            color="secondary"
            startIcon={!isConfigured ? <AddIcon /> : undefined}
            endIcon={isConfigured ? <ArrowForwardIcon /> : undefined}
            onClick={onAction}
            sx={{ mt: 1.5 }}
        >
            {actionLabel}
        </Button>
    </Paper>
)
